import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { rawMaterialService, supplierDirectoryService } from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

/** Текст помилок з відповіді DRF (поля + detail). HTML/traceback не показуємо. */
function formatDrfErrors(data) {
  if (data == null) return null;
  if (typeof data === 'string') {
    if (/<!DOCTYPE/i.test(data) || /<html/i.test(data) || /FieldError|Traceback/i.test(data)) {
      return 'Не вдалося зберегти. Спробуйте ще раз або зверніться до адміністратора.';
    }
    return data;
  }
  if (data.detail != null) {
    const d = Array.isArray(data.detail) ? data.detail.join('\n') : String(data.detail);
    if (/<!DOCTYPE/i.test(d) || /<html/i.test(d) || /FieldError|Traceback/i.test(d)) {
      return 'Не вдалося зберегти. Спробуйте ще раз або зверніться до адміністратора.';
    }
    return d;
  }
  const fieldLabels = {
    article: 'Артикул',
    name: '',
    type: 'Тип',
    supplier_name: 'Постачальник',
    unit_price: 'Базова ціна',
    minimum_stock: 'Мінімальний залишок',
    store_category: 'Склад',
    non_field_errors: '',
  };
  const lines = [];
  Object.keys(data).forEach((key) => {
    const v = data[key];
    if (v == null) return;
    const label = fieldLabels[key] != null ? fieldLabels[key] : key;
    if (Array.isArray(v)) {
      lines.push(label ? `${label}: ${v.join(' ')}` : v.join(' '));
    } else if (typeof v === 'object') {
      lines.push(label ? `${label}: ${JSON.stringify(v)}` : JSON.stringify(v));
    } else {
      lines.push(label ? `${label}: ${v}` : String(v));
    }
  });
  return lines.length ? lines.join('\n') : null;
}

function RawMaterialsPage() {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [usageDialog, setUsageDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    supplier_id: '',
    supplier_name: '',
    article: '',
    supplier_article: '',
    type: '',
    store_category: 'raw',
    description: '',
    minimum_stock: '',
    unit_price: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rmRes, supRes] = await Promise.all([
        rawMaterialService.getAll(),
        supplierDirectoryService.getAll({ is_active: true }).catch(() => ({ data: [] })),
      ]);
      setRawMaterials(rmRes.data.results || rmRes.data || []);
      const supList = Array.isArray(supRes.data) ? supRes.data : (supRes.data?.results || []);
      setSuppliers(supList.filter((s) => s.is_active !== false));
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveSupplierId = (supplierName) => {
    const n = String(supplierName || '').trim().toLowerCase();
    if (!n) return '';
    const hit = suppliers.find((s) => String(s.name || '').trim().toLowerCase() === n);
    return hit ? String(hit.id) : '';
  };

  const handleOpen = (rawMaterial = null) => {
    if (rawMaterial) {
      setEditing(rawMaterial.id);
      setFormData({
        name: rawMaterial.name,
        supplier_id: resolveSupplierId(rawMaterial.supplier_name),
        supplier_name: rawMaterial.supplier_name || '',
        article: rawMaterial.article || '',
        supplier_article: rawMaterial.supplier_article || '',
        type: rawMaterial.type,
        store_category: rawMaterial.store_category || 'raw',
        description: rawMaterial.description || '',
        minimum_stock:
          rawMaterial.minimum_stock != null && rawMaterial.minimum_stock !== ''
            ? String(rawMaterial.minimum_stock)
            : '',
        unit_price:
          rawMaterial.unit_price != null && rawMaterial.unit_price !== ''
            ? String(rawMaterial.unit_price)
            : '',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        supplier_id: '',
        supplier_name: '',
        article: '',
        supplier_article: '',
        type: '',
        store_category: 'raw',
        description: '',
        minimum_stock: '',
        unit_price: '',
      });
    }
    setOpen(true);
  };

  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (saving) return;
    try {
      const upStr = String(formData.unit_price ?? '').trim().replace(',', '.');
      let unit_price = null;
      if (upStr !== '') {
        const n = parseFloat(upStr);
        if (!Number.isFinite(n) || n < 0) {
          alert('Вкажіть коректну базову ціну (невід’ємне число) або залиште поле порожнім');
          return;
        }
        unit_price = n;
      }
      const articleTrimmed = String(formData.article ?? '').trim();
      const articleNorm = articleTrimmed === '' ? null : articleTrimmed;

      const existing =
        editing != null ? rawMaterials.find((m) => Number(m.id) === Number(editing)) : null;

      if (articleNorm != null) {
        const dup = rawMaterials.find(
          (m) =>
            String(m.article ?? '').trim() === articleNorm &&
            (!editing || Number(m.id) !== Number(editing)),
        );
        if (dup) {
          alert(
            `Артикул уже використовується в іншій позиції («${dup.name}»). Вкажіть інший артикул або залиште поле порожнім.`,
          );
          return;
        }
      }

      const selectedSupplier = suppliers.find(
        (s) => String(s.id) === String(formData.supplier_id || ''),
      );
      const supplierName = selectedSupplier
        ? (selectedSupplier.name || '').trim()
        : (formData.supplier_name || '').trim();

      const payload = {
        name: formData.name,
        supplier_name: supplierName,
        article: articleNorm,
        supplier_article: formData.supplier_article || '',
        type: formData.type,
        store_category: formData.store_category || 'raw',
        description: formData.description || '',
        unit_price,
        minimum_stock:
          formData.minimum_stock === '' || formData.minimum_stock == null
            ? null
            : Number(formData.minimum_stock),
        ...(existing && existing.quantity != null && existing.quantity !== ''
          ? { quantity: Number(existing.quantity) }
          : {}),
      };
      const nameTrim = (formData.name || '').trim().toLowerCase();
      if (nameTrim) {
        const nameDup = rawMaterials.find(
          (m) =>
            (m.name || '').trim().toLowerCase() === nameTrim &&
            (!editing || Number(m.id) !== Number(editing)),
        );
        if (nameDup) {
          alert('Така назва вже існує');
          return;
        }
      }
      // Перевірка на злиття (схожі назва/постачальник)
      const supNameTrim = (formData.supplier_name || '').trim().toLowerCase();
      const mergeMatch = rawMaterials.find((m) => {
        if (editing && Number(m.id) === Number(editing)) return false;
        const mName = (m.name || '').trim().toLowerCase();
        const mSupName = (m.supplier_name || '').trim().toLowerCase();
        return (supNameTrim && mName && supNameTrim === mName) ||
               (nameTrim && mSupName && nameTrim === mSupName);
      });
      if (mergeMatch) {
        setMergeCandidate(mergeMatch);
        setPendingPayload({ payload, editing });
        return;
      }

      setSaving(true);
      if (editing) {
        await rawMaterialService.update(editing, payload);
      } else {
        await rawMaterialService.create(payload);
      }
      setOpen(false);
      setEditing(null);
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg =
        error.userMessage ||
        formatDrfErrors(error.response?.data) ||
        error.response?.statusText ||
        error.message ||
        'Помилка збереження даних';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeparate = async () => {
    if (!pendingPayload || saving) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    setSaving(true);
    try {
      if (ed) { await rawMaterialService.update(ed, payload); }
      else { await rawMaterialService.create(payload); }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка збереження';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeCandidate || !pendingPayload || saving) return;
    const { payload, editing: ed } = pendingPayload;
    const candidate = mergeCandidate;
    setMergeCandidate(null);
    setPendingPayload(null);
    setSaving(true);
    try {
      const merged = {
        ...payload,
        name: payload.name || candidate.name || '',
        supplier_name: payload.supplier_name || candidate.supplier_name || '',
        article: payload.article || candidate.article || null,
        supplier_article: payload.supplier_article || candidate.supplier_article || '',
        type: payload.type || candidate.type || '',
        description: payload.description || candidate.description || '',
        unit_price: payload.unit_price ?? candidate.unit_price ?? null,
        minimum_stock: payload.minimum_stock ?? candidate.minimum_stock ?? null,
        store_category: payload.store_category || candidate.store_category || 'raw',
      };
      if (ed) {
        await rawMaterialService.update(ed, merged);
        await rawMaterialService.delete(candidate.id);
      } else {
        await rawMaterialService.update(candidate.id, merged);
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка злиття';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const item = rawMaterials.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(rawMaterialService, id, {
        itemName,
        confirmMessage: 'Ви впевнені, що хочете видалити цю сировину?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await rawMaterialService.delete(id);
      loadData();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      console.error('Помилка видалення:', error);
      alert(formatDrfErrors(data) || 'Помилка видалення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageBackHeader />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Сировина</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <EntityImportExportButtons entityKey="raw_materials" label="Сировина" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Додати сировину
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Назва постачальника</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Артикул постачальника</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Склад</TableCell>
              <TableCell>Ціна (грн)</TableCell>
              <TableCell>Мін. залишок</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rawMaterials.map((rawMaterial) => (
              <TableRow key={rawMaterial.id}>
                <TableCell>{rawMaterial.name}</TableCell>
                <TableCell>{rawMaterial.supplier_name || '—'}</TableCell>
                <TableCell>{rawMaterial.article || '—'}</TableCell>
                <TableCell>{rawMaterial.supplier_article || '—'}</TableCell>
                <TableCell>{rawMaterial.type}</TableCell>
                <TableCell>
                  {rawMaterial.store_category === 'technical'
                    ? 'Техматеріали'
                    : 'Сировина'}
                </TableCell>
                <TableCell>{rawMaterial.unit_price != null ? rawMaterial.unit_price : '—'}</TableCell>
                <TableCell>{rawMaterial.minimum_stock != null ? rawMaterial.minimum_stock : '—'}</TableCell>
                <TableCell>{rawMaterial.description || '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(rawMaterial)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(rawMaterial.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати сировину' : 'Додати сировину'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Назва"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Постачальник"
              value={formData.supplier_id || ''}
              onChange={(e) => {
                const id = e.target.value;
                const s = suppliers.find((x) => String(x.id) === String(id));
                setFormData({
                  ...formData,
                  supplier_id: id,
                  supplier_name: s ? (s.name || '') : '',
                });
              }}
              fullWidth
              helperText="З довідника постачальників; можна залишити порожнім"
            >
              <MenuItem value="">— без постачальника —</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Артикул"
              value={formData.article}
              onChange={(e) => setFormData({ ...formData, article: e.target.value })}
              fullWidth
              helperText="Унікальний серед сировини; можна літери й цифри. Порожньо — без артикула"
            />
            <TextField
              label="Артикул постачальника"
              value={formData.supplier_article}
              onChange={(e) => setFormData({ ...formData, supplier_article: e.target.value })}
              fullWidth
            />
            <TextField
              label="Тип"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Склад обліку залишку"
              value={formData.store_category || 'raw'}
              onChange={(e) => setFormData({ ...formData, store_category: e.target.value })}
              fullWidth
              helperText="Визначає, на якій сторінці складу відображається позиція"
            >
              <MenuItem value="raw">Склад сировини</MenuItem>
              <MenuItem value="technical">Склад техматеріалів</MenuItem>
            </TextField>
            <TextField
              label="Базова ціна (грн / кг)"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
              fullWidth
              helperText="Та сама ціна, що в розділі «Довідник цін матеріалів»; порожньо — без базової ціни"
            />
            <TextField
              label="Мінімальний залишок на складі (для критичних залишків на головній)"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.minimum_stock}
              onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
              fullWidth
              helperText="Порожньо — не використовувати поріг з довідника"
            />
            <TextField
              label="Опис"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Діалог злиття */}
      <Dialog open={Boolean(mergeCandidate)} onClose={() => { if (!saving) { setMergeCandidate(null); setPendingPayload(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>Знайдено схожу позицію</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Назва або назва постачальника збігається з існуючою позицією:
          </Typography>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 1 }}>
            <Typography><b>Назва:</b> {mergeCandidate?.name || '—'}</Typography>
            <Typography><b>Назва постачальника:</b> {mergeCandidate?.supplier_name || '—'}</Typography>
            <Typography><b>Артикул:</b> {mergeCandidate?.article || '—'}</Typography>
            <Typography><b>Артикул постачальника:</b> {mergeCandidate?.supplier_article || '—'}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Об'єднати в одну картку (пусті поля заповняться з іншої позиції) або зберегти як окремі?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeCandidate(null); setPendingPayload(null); }} disabled={saving}>Скасувати</Button>
          <Button onClick={handleSaveSeparate} variant="outlined" disabled={saving}>Зберегти окремо</Button>
          <Button onClick={handleMerge} variant="contained" color="primary" disabled={saving}>Об'єднати</Button>
        </DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default RawMaterialsPage;
